import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { Chess } from 'chess.js';

import CCColors from '@/Utils/CCColors';
import { FontSizes } from '@/Utils/Dimensions';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import ChessboardWithArrow from '@/Components/ChessboardWithArrow';
import useVerifyPuzzlesApi from '@/Hooks/PuzzlesApi/verifyPuzzlesApi';
import useGetChallengePuzzles from '@/Hooks/RenewalApi/useGetChallengePuzzles';
import useGeneratePuzzleCoupon from '@/Hooks/RenewalApi/useGeneratePuzzleCoupon';
import PuzzleMoveResult from './components/PuzzleMoveResult';
import PuzzleChallengeResult from './components/PuzzleChallengeResult';
import { saveChallengeState, loadChallengeState, clearChallengeState } from './utils';
import { ChallengePhase, ChallengePuzzle } from './types';
import { ScreenName } from '@/navigators/StackConstants';
import useLoggedInUserStore from '@/Store/loggedInUserstore';

export default function RenewalPuzzleChallenge() {
  const navigation = useNavigation<any>();
  const userId = useLoggedInUserStore(state => state.userId) || 'guest';

  // ── State ──────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState<ChallengePhase>('loading');
  const [puzzles, setPuzzles] = useState<ChallengePuzzle[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [solvedCount, setSolvedCount] = useState(0);
  const [lastMoveCorrect, setLastMoveCorrect] = useState(false);
  const [boardPosition, setBoardPosition] = useState('');
  const [previousFen, setPreviousFen] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponExpiresAt, setCouponExpiresAt] = useState('');
  const [isGeneratingCoupon, setIsGeneratingCoupon] = useState(false);

  const chessRef = useRef<Chess | null>(null);
  // Track solved count in a ref so it's accessible in async callbacks without stale closure
  const solvedCountRef = useRef(0);

  // ── API Hooks ───────────────────────────────────────────────────────────
  const { mutate: fetchPuzzles } = useGetChallengePuzzles(
    puzzleList => {
      if (puzzleList.length === 0) {
        // No puzzles available — go straight to complete with 0 solved
        setPhase('complete');
        return;
      }
      setPuzzles(puzzleList);
      loadPuzzle(puzzleList, 0);
      setPhase('puzzle');
      // Persist puzzle list so partial progress can be restored on refresh
      saveChallengeState(userId, {
        completed: false,
        solvedCount: 0,
        currentIndex: 0,
        puzzles: puzzleList,
      });
    },
    () => setPhase('complete'),
  );

  const { mutate: verifyPuzzle } = useVerifyPuzzlesApi(
    data => {
      // data.success is true when the move was the best response
      const correct = Boolean(data?.success);
      if (correct) {
        solvedCountRef.current += 1;
        setSolvedCount(solvedCountRef.current);
      }
      setLastMoveCorrect(correct);
      setPhase('move_result');
    },
  );

  const { mutate: generateCoupon } = useGeneratePuzzleCoupon(
    data => {
      setCouponCode(data.coupon_code);
      setCouponDiscount(data.discount_percentage);
      setCouponExpiresAt(data.expires_at);
      setIsGeneratingCoupon(false);

      // Persist to localStorage
      saveChallengeState(userId, {
        completed: true,
        solvedCount: solvedCountRef.current,
        couponCode: data.coupon_code,
        couponDiscount: data.discount_percentage,
        couponExpiresAt: data.expires_at,
      });
    },
    () => setIsGeneratingCoupon(false),
  );

  // ── Lifecycle ───────────────────────────────────────────────────────────
  useEffect(() => {
    const stored = loadChallengeState(userId);

    // Already completed with a coupon — show results directly
    if (stored?.completed) {
      solvedCountRef.current = stored.solvedCount ?? 0;
      setSolvedCount(stored.solvedCount ?? 0);
      setCouponCode(stored.couponCode ?? '');
      setCouponDiscount(stored.couponDiscount ?? 0);
      setCouponExpiresAt(stored.couponExpiresAt ?? '');
      setPhase('complete');
      return;
    }

    // Partial progress — restore puzzles and resume from last index
    if (stored?.puzzles?.length && stored.currentIndex !== undefined) {
      const idx = stored.currentIndex;
      solvedCountRef.current = stored.solvedCount ?? 0;
      setSolvedCount(stored.solvedCount ?? 0);
      setPuzzles(stored.puzzles);
      setCurrentIndex(idx);
      loadPuzzle(stored.puzzles, idx);
      setPhase('puzzle');
      return;
    }

    fetchPuzzles();
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const loadPuzzle = (puzzleList: ChallengePuzzle[], index: number) => {
    const puzzle = puzzleList[index];
    if (!puzzle) return;
    chessRef.current = new Chess(puzzle.fen);
    setBoardPosition(puzzle.fen);
    setPreviousFen(puzzle.previousFen);
  };

  const advanceToNextPuzzle = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= puzzles.length) {
      const finalSolvedCount = solvedCountRef.current;
      setPhase('complete');
      if (finalSolvedCount >= 5) {
        setIsGeneratingCoupon(true);
        generateCoupon({ solved_count: finalSolvedCount });
        // completed state with coupon is saved inside generateCoupon callback
      } else {
        // Scored < 5: don't lock them out — clear so they can retry
        clearChallengeState(userId);
      }
    } else {
      setCurrentIndex(nextIndex);
      loadPuzzle(puzzles, nextIndex);
      setPhase('puzzle');
      // Persist partial progress so refresh restores here
      saveChallengeState(userId, {
        completed: false,
        solvedCount: solvedCountRef.current,
        currentIndex: nextIndex,
        puzzles,
      });
    }
  }, [currentIndex, puzzles, generateCoupon, userId]);

  // ── Move Handler ─────────────────────────────────────────────────────────
  const handleMove = (
    from: string,
    to: string,
    promotion?: string,
  ): boolean => {
    if (phase !== 'puzzle' || !chessRef.current) return false;

    const moveResult = chessRef.current.move({
      from,
      to,
      promotion: (promotion as any) || 'q',
    });
    if (!moveResult) return false; // Illegal move — board snaps back

    setBoardPosition(chessRef.current.fen());
    setPreviousFen(puzzles[currentIndex]?.fen || '');
    setPhase('verifying');
    verifyPuzzle({
      puzzleId: String(puzzles[currentIndex].id),
      move: moveResult.lan,
    });

    return false; // Let ChessboardWithArrow handle internal rendering
  };

  // ── Retry ────────────────────────────────────────────────────────────────
  const handleRetry = () => {
    clearChallengeState(userId);
    solvedCountRef.current = 0;
    setSolvedCount(0);
    setCurrentIndex(0);
    setPuzzles([]);
    setPhase('loading');
    fetchPuzzles();
  };

  // ── Renew Now ────────────────────────────────────────────────────────────
  const handleRenewNow = () => {
    navigation.navigate(ScreenName.Renewal, {
      couponCode,
      // couponDiscountPercentage: couponDiscount,
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (phase === 'loading') {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={CCColors.Primary.Default} />
        <CCText
          fontSize={FontSizes[14]}
          color={CCColors.TextColor.Grey}
          style={styles.loadingText}>
          Loading your puzzles…
        </CCText>
      </View>
    );
  }

  if (phase === 'complete') {
    return (
      <PuzzleChallengeResult
        solvedCount={solvedCountRef.current}
        totalPuzzles={puzzles.length || 8}
        couponCode={couponCode || undefined}
        couponDiscount={couponDiscount || undefined}
        couponExpiresAt={couponExpiresAt || undefined}
        isGeneratingCoupon={isGeneratingCoupon}
        onRetry={handleRetry}
        onRenewNow={handleRenewNow}
        onBack={() => navigation.goBack()}
      />
    );
  }

  const currentPuzzle = puzzles[currentIndex];
  if (!currentPuzzle) return null;

  return (
    <View style={styles.screen}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backBtn}>
          <CCText fontSize={FontSizes[20]} color={CCColors.White}>←</CCText>
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          <CCText fontSize={FontSizes[16]} fontWeight="bold" color={CCColors.White}>
            🎯 Discount Challenge
          </CCText>
          <CCText fontSize={FontSizes[11]} color="rgba(255,255,255,0.75)">
            1 correct answer = 1% off your renewal
          </CCText>
        </View>
        <View style={styles.scoreChip}>
          <CCText fontSize={FontSizes[18]} fontWeight="bold" color={CCColors.Primary.Default}>
            {solvedCount}
          </CCText>
          <CCText fontSize={FontSizes[13]} color="rgba(255,255,255,0.7)">
            /{puzzles.length}
          </CCText>
        </View>
      </View>

      {/* Progress bar + label */}
      <View style={styles.progressSection}>
        <View style={styles.progressMeta}>
          <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey}>
            Puzzle {currentIndex + 1} / {puzzles.length}
          </CCText>
          <CCText fontSize={FontSizes[12]} color={CCColors.Primary.Brown} fontWeight="bold">
            {currentPuzzle.colorToMove === 'black' ? '⬛ Black' : '⬜ White'} to move
          </CCText>
        </View>
        <View style={styles.progressRow}>
          {puzzles.map((_, i) => (
            <View
              key={i}
              style={[
                styles.progressSegment,
                i < currentIndex && styles.progressDone,
                i === currentIndex && styles.progressCurrent,
              ]}
            />
          ))}
        </View>
      </View>

      {/* Chess board */}
      <View style={styles.boardWrapper}>
        <ChessboardWithArrow
          position={boardPosition}
          onPieceDropExtension={handleMove}
          lastMoveFen={previousFen}
          boardOrientation={currentPuzzle.colorToMove}
          colorTheme="green-white"
        />
        {(phase === 'move_result' || phase === 'verifying') &&
          (phase === 'move_result' ? (
            <PuzzleMoveResult
              isCorrect={lastMoveCorrect}
              onDismiss={advanceToNextPuzzle}
            />
          ) : (
            <View style={styles.verifyingOverlay}>
              <ActivityIndicator color={CCColors.Primary.Default} />
            </View>
          ))}
      </View>

      {/* Context */}
      <View style={styles.contextBox}>
        <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.Grey} style={styles.contextLabel}>
          FROM YOUR GAME
        </CCText>
        <CCText fontSize={FontSizes[14]} fontWeight="bold" color={CCColors.Primary.Brown}>
          vs. {currentPuzzle.opponent}
        </CCText>
        {currentPuzzle.openingMainline ? (
          <CCText fontSize={FontSizes[12]} color={CCColors.TextColor.Grey} style={styles.contextSub}>
            {currentPuzzle.openingMainline}
          </CCText>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: CCColors.Primary.Light },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: CCColors.Primary.Light,
    gap: 12,
  },
  loadingText: { marginTop: 12 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    paddingHorizontal: 16,
    backgroundColor: CCColors.Primary.Brown,
  },
  backBtn: { padding: 4, marginRight: 8 },
  headerCenter: { flex: 1 },
  scoreChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  progressSection: {
    backgroundColor: CCColors.Background,
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: CCColors.CCTableBg,
  },
  progressMeta: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  progressRow: {
    flexDirection: 'row',
    gap: 4,
  },
  progressSegment: {
    flex: 1,
    height: 6,
    backgroundColor: CCColors.CCTableBg,
    borderRadius: 3,
  },
  progressDone: { backgroundColor: CCColors.Green },
  progressCurrent: { backgroundColor: CCColors.Primary.Default },
  boardWrapper: {
    marginHorizontal: 16,
    marginTop: 12,
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    aspectRatio: 1,
    width: '100%',
    maxWidth: 500,
    alignSelf: 'center',
    backgroundColor: CCColors.Background,
    shadowColor: CCColors.Primary.Brown,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  verifyingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.6)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
    borderRadius: 12,
  },
  contextBox: {
    margin: 16,
    marginTop: 12,
    backgroundColor: CCColors.Background,
    borderRadius: 8,
    padding: 12,
    borderLeftWidth: 3,
    borderLeftColor: CCColors.Primary.Default,
    borderWidth: 1,
    borderColor: CCColors.CCTableBg,
  },
  contextLabel: {
    letterSpacing: 1,
    marginBottom: 3,
  },
  contextSub: { marginTop: 3 },
});
